// Currency formatting utilities for West African CFA francs (XOF)

/**
 * Formats a number as XOF currency string
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "1,500.00 XOF")
 */
export const formatXOF = (amount: number): string => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0.00 XOF';
  }
  
  return new Intl.NumberFormat('fr-BF', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount) + ' XOF';
};

/**
 * Parses XOF currency string back to number
 * @param xofString - XOF formatted string (e.g., "1,500.00 XOF")
 * @returns Numeric value
 */
export const parseXOF = (xofString: string): number => {
  if (!xofString || typeof xofString !== 'string') {
    return 0;
  }
  
  // Remove XOF suffix and spaces, then parse
  const numericString = xofString.replace(/\s*XOF\s*$/i, '').replace(/,/g, '');
  const parsed = parseFloat(numericString);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Gets the currency code
 * @returns Currency code string
 */
export const getCurrencyCode = (): string => {
  return 'XOF';
};

/**
 * Checks if a value is already XOF formatted
 * @param value - Value to check
 * @returns True if already formatted as XOF
 */
export const isXOFFormatted = (value: any): boolean => {
  return typeof value === 'string' && value.includes('XOF');
};

/**
 * Smart format function that handles both numbers and XOF strings
 * @param value - Number or XOF string
 * @returns XOF formatted string
 */
export const smartFormatXOF = (value: number | string): string => {
  if (isXOFFormatted(value)) {
    return value as string;
  }
  
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return formatXOF(parsed);
  }
  
  return formatXOF(value);
};
